/**
 * useProjectBootstrap — единая точка синхронизации currentProjectId (store)
 * со списком проектов с бэкенда.
 *
 * Зачем (Block 5 / C-блок + регрессия 2026-07-26):
 *  - В localStorage может лежать stale UUID (проект из прошлой сессии
 *    удалён / user переключился / pytest TRUNCATE-баг).
 *  - Если страница (FilesUpload, FilesResults, search/generation)
 *    идёт в `useProjectDatasets(staleId)` первой — получает 404.
 *  - ProjectPicker в Header не успевает отрезолвить, если Header
 *    смонтирован позже или Picker не активен.
 *
 * Mount: 1 раз в Shell (после AuthGuard, до Outlet-страниц).
 *
 * Логика:
 *  1. fetch /projects   (TanStack Query через useProjects)
 *  2. как только list готов:
 *     - currentId НЕ в списке AND list не пуст → reset + взять (по приоритету):
 *         1) project c name === "Default"
 *         2) первый non-archived
 *         3) первый (fallback)
 *     - currentId is null AND list не пуст → взять (тот же приоритет)
 *  3. list пуст → lazy-create "Default" через POST /projects, сетаем в store.
 *  4. toast «Проект сброшен» если был stale id (i18n header.projectPicker.resetToast).
 *  5. никогда не вызываем API со stale id (Pages берут currentProjectId из store,
 *     который уже валиден на момент первого render после bootstrap).
 *
 * Не использует setState в render — только useEffect.
 * Tenant 404 (cross-tenant) обрабатывается BE: его тут не трогаем.
 *
 * Важно (после регрессии 2026-07-26 / ProjectPicker switch+create):
 *  - Picker подписан на useProjectStore напрямую. Любой выбор в Picker
 *    обновляет store + URL атомарно (см. ProjectPicker.useSyncUrlProject).
 *  - Bootstrap здесь НЕ перетирает валидный выбор пользователя:
 *    `exists` гард (см. ниже) срабатывает первым, и до pick() мы
 *    не доходим.
 */
import { useEffect, useRef } from 'react';
import { useTranslation } from 'react-i18next';

import { useProjectStore } from '@/store/projectStore';
import { useCreateProject, useProjects, type Project } from '@/hooks/useProjects';

const DEFAULT_PROJECT_NAME = 'Default';

/**
 * Интерфейс результата bootstrap — возвращается компоненту для показа toast.
 * При желании Shell может подписаться через custom subscriber.
 */
export interface ProjectBootstrapResult {
  status: 'idle' | 'loading' | 'ready' | 'reset-and-picked' | 'lazy-created';
  currentProjectId: string | null;
  resetReason: 'stale-id' | 'no-projects' | null;
}

export function useProjectBootstrap(): void {
  const { t } = useTranslation();
  const currentId = useProjectStore((s) => s.currentProjectId);
  const setCurrent = useProjectStore((s) => s.setCurrent);

  const { data, isFetched } = useProjects();
  const createProject = useCreateProject();

  // Защита от двойного запуска lazy-create при StrictMode / re-renders.
  const lazyCreateInFlight = useRef(false);

  const projects: Project[] = data?.items ?? [];

  useEffect(() => {
    // Ждём ответ от /projects (даже isLoading=true ⇒ isFetched=false).
    if (!isFetched) return;

    // === Случай 1: список пуст → lazy-create Default ===
    if (projects.length === 0) {
      if (currentId !== null) {
        // store помнит stale id — сбросить ДО создания, иначе pages запросят datasets по нему.
        setCurrent(null);
      }
      if (lazyCreateInFlight.current) return;
      if (createProject.isPending) return;
      lazyCreateInFlight.current = true;
      void createProject
        .mutateAsync(DEFAULT_PROJECT_NAME)
        .then((created) => {
          setCurrent(created.id);
        })
        .catch((err) => {
          // eslint-disable-next-line no-console
          console.error('[useProjectBootstrap] lazy-create failed', err);
        })
        .finally(() => {
          lazyCreateInFlight.current = false;
        });
      return;
    }

    // === Случай 2: есть projects ===
    const exists = projects.some((p) => p.id === currentId);
    if (exists) return; // всё ок, current валиден

    // currentId null или stale → выбрать по приоритету.
    const pick = (): Project | undefined => {
      const def = projects.find((p) => p.name === DEFAULT_PROJECT_NAME);
      if (def) return def;
      const firstActive = projects.find((p) => !p.is_archived);
      if (firstActive) return firstActive;
      return projects[0];
    };

    const target = pick();
    if (!target) return;

    // eslint-disable-next-line no-console
    console.info(
      '[useProjectBootstrap] resetting stale projectId; picked',
      target.id,
      'reason:',
      currentId ? 'stale-id' : 'no-current',
    );
    // Picker подписан на useProjectStore; не перетираем его выборку,
    // а только подменяем stale id. Если currentId валиден (см. exists выше) —
    // до сюда не доходим.
    setCurrent(target.id);

    // Toast (fire-and-forget) — i18n key слишком длинный для console.warn,
    // используется только в dev для отладки.
    if (currentId) {
      // eslint-disable-next-line no-console
      console.info(
        t('header.projectPicker.resetToast', {
          defaultValue: 'Проект сброшен — выбран «{name}».',
          name: target.name,
        }),
      );
    }
  }, [
    isFetched,
    projects,
    currentId,
    setCurrent,
    createProject,
    t,
  ]);

  // refetch НЕ нужен — useProjects уже invalidate'ит через qc.
  // При удалении проекта через другой таб useProjects сам перезагружает
  // список, и useEffect выше подхватит новое состояние.
}
